import { FC } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThumbsDown } from '@fortawesome/free-regular-svg-icons'
import { ReactComponent as GlobeIcon } from 'src/assets/svg/akar-icons_globe.svg'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { ReactComponent as MentionIcon } from 'src/assets/svg/akar-icons_mention.svg'
import { ReactComponent as SmileIcon } from 'src/assets/svg/uil_smile.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { cn } from 'src/utils/cn'
import styles from './multiKeyMetrics.module.scss'
import { MetricIcon, MultiCampaign, MultiMetricRow } from '../interfaces'
import { campaignColor } from '../palette'

interface MultiKeyMetricsProps {
  campaigns: MultiCampaign[]
  insight: string[]
  metrics: MultiMetricRow[]
}

const COPY = {
  metric: 'Metric',
  subtitle:
    'Track how key performance indicators changed between the selected periods.',
  title: 'Key Metrics Comparison',
}

// Named rather than positional: with four rows that can be reordered, keying
// the icon to an index is a bug waiting for the first reorder.
const ICONS: Record<MetricIcon, FC<{ className?: string }>> = {
  mentions: MentionIcon,
  negative: () => (
    <FontAwesomeIcon className={styles.rowIcon} icon={faThumbsDown} />
  ),
  sentiment: SmileIcon,
  sources: GlobeIcon,
}

// A table rather than a row of cards: at four campaigns the two-campaign card
// layout would give each metric four figures side by side with no shared axis
// to read them against. A column per campaign gives every row the same one.
export const MultiKeyMetrics: FC<MultiKeyMetricsProps> = ({
  campaigns,
  insight,
  metrics,
}) => {
  // One template drives the heading and every row, so a cell can never land
  // under the wrong campaign.
  const columns = {
    gridTemplateColumns: `repeat(${campaigns.length + 1}, minmax(0, 1fr))`,
  }

  return (
    <>
      <section className={styles.card}>
        <div className={styles.header}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{COPY.subtitle}</p>
        </div>

        <div className={styles.table}>
          <div className={styles.headRow} style={columns}>
            <span className={styles.headMetric}>{COPY.metric}</span>

            {campaigns.map((campaign, index) => (
              <span className={styles.headCampaign} key={campaign.id}>
                <span
                  className={styles.headDot}
                  style={{ background: campaignColor(index) }}
                />
                <TruncatedTooltip
                  className={styles.headName}
                  maxLines={2}
                  text={campaign.name}
                />
              </span>
            ))}
          </div>

          {metrics.map((row) => {
            const Icon = ICONS[row.icon]

            return (
              <div className={styles.row} key={row.label} style={columns}>
                <span className={styles.rowLabel}>
                  <Icon className={styles.rowIcon} />
                  {row.label}
                </span>

                {row.cells.map((cell, index) => (
                  <span
                    // The cell belongs to the campaign in this column, and a
                    // campaign appears once, so its id is the key.
                    className={cn(
                      styles.cell,
                      cell.tone === 'good' && styles.cellGood,
                      cell.tone === 'bad' && styles.cellBad
                    )}
                    key={campaigns[index]?.id ?? index}
                  >
                    <span className={styles.cellValue}>{cell.value}</span>

                    {cell.badge && (
                      <span
                        className={cn(
                          styles.badge,
                          cell.tone === 'bad'
                            ? styles.badgeBad
                            : styles.badgeGood
                        )}
                      >
                        {cell.badge}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )
          })}
        </div>
      </section>

      {/* Figma sets this 8px under the table rather than the 24 the page uses
          elsewhere: the insight is about the table, not a section beside it. */}
      <section className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />

        <div className={styles.insightText}>
          {insight.map((paragraph) => (
            <p className={styles.insightParagraph} key={paragraph}>
              {paragraph}
            </p>
          ))}
        </div>
      </section>
    </>
  )
}
